// Phase 3C.3 — the client-side reminder-configuration mirror.
//
// Mirrors backend/quowada/quotation_reminders.py, which itself mirrors
// private.is_valid_reminder_offset_set. THE DATABASE IS AUTHORITATIVE: the
// CHECK constraint on public.organizations and public.sync_quotation_reminders
// both refuse anything this file would let through. This exists so a user gets
// an immediate Bulgarian sentence instead of a round trip for an obvious
// mistake, and so the offset wording is written in exactly one place.
//
// Nothing here computes a schedule. 09:00 Europe/Sofia on
// (valid_until - days_before) is decided by the database and arrives as a
// timestamp; this file only labels and formats what it is given.

export const MIN_DAYS_BEFORE = 0;
export const MAX_DAYS_BEFORE = 90;
export const MAX_OFFSETS = 5;

export const REMINDER_TIMEZONE = "Europe/Sofia";
export const REMINDER_SEND_TIME_LOCAL = "09:00";

// Mirrors quowada.quotation_reminders.MSG — same keys, same sentences.
export const REMINDER_MESSAGES = {
  offsets_invalid: "Графикът за напомняния трябва да е списък от дни.",
  offset_not_an_integer: "Напомнянията се задават в цели дни преди изтичане.",
  offset_out_of_range: "Напомнянията се задават между 0 и 90 дни преди изтичане.",
  offsets_duplicate: "Един и същ ден не може да бъде добавен два пъти.",
  offsets_too_many: "Може да зададете най-много 5 напомняния.",
  quotation_terminal:
    "Офертата е приключена — напомнянията ѝ вече не могат да се променят.",
  save_failed: "Напомнянията не бяха запазени. Опитайте отново.",
};

// The safe UI states the API returns. The database enum never reaches here,
// and neither does an attempt counter, a provider error code or a payload.
export const REMINDER_STATES = {
  scheduled: "scheduled",
  sending: "sending",
  sent: "sent",
  skipped: "skipped",
  missed: "missed",
  cancelled: "cancelled",
  // A `failed` database row means one of three different things to a person,
  // so the API splits it: `retrying` = another attempt really is coming,
  // `failed` = none ever will (permanent error, attempts exhausted, or the
  // 24h send window closed first).
  retrying: "retrying",
  failed: "failed",
};

// Quotation-level delivery state, so the card can explain WHY there is no next
// reminder instead of silently showing nothing.
export const DELIVERY_STATES = {
  active: "active",
  awaiting_send: "awaiting_send",
  lapsed: "lapsed",
  stopped: "stopped",
};

export const DELIVERY_STATE_NOTES = {
  awaiting_send:
    "Напомнянията ще започнат след като офертата бъде изпратена на клиента.",
  lapsed:
    "Срокът на офертата е изтекъл, затова повече напомняния няма да бъдат изпращани.",
  stopped:
    "Офертата е приключена. Напомнянията се показват само за история.",
};

// Bulgarian for each state. `missed` and `skipped` read differently on purpose:
// "skipped" is a rule that was configured after its window had already passed,
// "missed" is one whose send window closed before the worker could deliver it.
// Neither is an error, and neither is ever presented as one.
export const REMINDER_STATE_LABELS = {
  scheduled: "Насрочено",
  sending: "Изпраща се",
  sent: "Изпратено",
  skipped: "Пропуснато (срокът беше минал)",
  missed: "Пропуснато (срокът за изпращане изтече)",
  cancelled: "Отменено",
  retrying: "Неуспешно — ще бъде опитано отново",
  failed: "Неуспешно — няма да бъде опитано отново",
};

// A rule in one of these states is history: it is shown, never edited, and no
// control may suggest it can be re-armed. A sent offset in particular can NEVER
// come back — that is a Phase 3C.1 database invariant, not a UI preference.
export const HISTORICAL_STATES = [
  "sent",
  "skipped",
  "missed",
  "cancelled",
  // A permanently failed rule is history too: no further attempt exists, and
  // no control may suggest one can be arranged.
  "failed",
];

export const isHistoricalState = (state) => HISTORICAL_STATES.includes(state);

/** "В деня на изтичане" for 0, "1 ден преди изтичане", "N дни преди изтичане". */
export function offsetLabel(days) {
  const n = Number(days);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "В деня на изтичане";
  if (n === 1) return "1 ден преди изтичане";
  return `${n} дни преди изтичане`;
}

/** The short chip form: "В деня", "7 дни преди". */
export function offsetChipLabel(days) {
  const n = Number(days);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "В деня на изтичане";
  if (n === 1) return "1 ден преди";
  return `${n} дни преди`;
}

/** Parse one typed offset. Returns `{ value }` or `{ error }`. */
export function parseOffset(raw) {
  const text = String(raw ?? "").trim();
  if (!text || !/^\d{1,3}$/.test(text)) {
    return { error: REMINDER_MESSAGES.offset_not_an_integer };
  }
  const value = Number(text);
  if (value < MIN_DAYS_BEFORE || value > MAX_DAYS_BEFORE) {
    return { error: REMINDER_MESSAGES.offset_out_of_range };
  }
  return { value };
}

/**
 * Validate a whole offset list. Returns a Bulgarian message or `null`.
 * The check order matches the backend so the two never disagree about which
 * mistake a user is told about first.
 */
export function validateOffsets(list) {
  if (!Array.isArray(list)) return REMINDER_MESSAGES.offsets_invalid;
  for (const item of list) {
    if (!Number.isInteger(item)) return REMINDER_MESSAGES.offset_not_an_integer;
    if (item < MIN_DAYS_BEFORE || item > MAX_DAYS_BEFORE) {
      return REMINDER_MESSAGES.offset_out_of_range;
    }
  }
  if (new Set(list).size !== list.length) return REMINDER_MESSAGES.offsets_duplicate;
  if (list.length > MAX_OFFSETS) return REMINDER_MESSAGES.offsets_too_many;
  return null;
}

/** Add one offset to a list, keeping it sorted descending. Returns `{list}`/`{error}`. */
export function addOffset(list, raw) {
  const current = Array.isArray(list) ? list : [];
  const parsed = parseOffset(raw);
  if (parsed.error) return { error: parsed.error };
  if (current.includes(parsed.value)) {
    return { error: REMINDER_MESSAGES.offsets_duplicate };
  }
  if (current.length >= MAX_OFFSETS) {
    return { error: REMINDER_MESSAGES.offsets_too_many };
  }
  return { list: [...current, parsed.value].sort((a, b) => b - a) };
}

export function removeOffset(list, days) {
  return (Array.isArray(list) ? list : []).filter((d) => d !== days);
}

/** True when two offset lists mean the same configuration (order-insensitive). */
export function sameOffsets(a, b) {
  const left = [...(a || [])].sort((x, y) => x - y);
  const right = [...(b || [])].sort((x, y) => x - y);
  return left.length === right.length && left.every((v, i) => v === right[i]);
}

/**
 * Bulgarian text for the reminder API's stable slugs. Anything unknown falls
 * back to the caller's message — a raw slug or an SQL string is never shown.
 */
const API_ERROR_MESSAGES = {
  quotation_terminal: REMINDER_MESSAGES.quotation_terminal,
  reminder_sync_failed: REMINDER_MESSAGES.save_failed,
  reminder_defaults_save_failed: REMINDER_MESSAGES.save_failed,
};

export function reminderApiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    if (detail.errors?.days_before) return detail.errors.days_before;
    if (detail.code && API_ERROR_MESSAGES[detail.code]) {
      return API_ERROR_MESSAGES[detail.code];
    }
    if (typeof detail.message === "string" && detail.code) return detail.message;
  }
  return fallback || REMINDER_MESSAGES.save_failed;
}
